// src/Redux/slices/sharingSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  shareDocument,
  shareDocuments,
  fetchDocumentAccessList,
  revokeDocumentAccess,
  revokeAllAccessForDoctor,
  extendDocumentAccess,
  getDoctorSharedDocuments,
  getPatientDoctorsWithAccess,
  getSharingStatistics,
} from "../thunks/sharingthunks";

const initialState = {
  // Map: documentId -> accessList[]
  accessByDocument: {},
  // Map: documentId -> { loading, error }
  accessStatusByDocument: {},

  // Liste documents partagés avec le médecin connecté
  doctorSharedDocuments: [],
  doctorSharedDocumentsTotal: 0,
  doctorSharedDocumentsLoading: false,
  doctorSharedDocumentsError: null,

  // Médecins ayant accès aux documents du patient
  patientDoctorsWithAccess: [],
  patientDoctorsWithAccessLoading: false,
  patientDoctorsWithAccessError: null,

  // Statistiques de partage
  sharingStatistics: null,
  sharingStatisticsLoading: false,
  sharingStatisticsError: null,

  // État global share
  shareLoading: false,
  shareError: null,
  lastShareResult: null,

  // État global revoke
  revokeLoading: false,
  revokeError: null,

  // Extend access
  extendLoading: false,
  extendError: null,
};

const sharingSlice = createSlice({
  name: "sharing",
  initialState,
  reducers: {
    clearShareError(state) {
      state.shareError = null;
    },
    clearRevokeError(state) {
      state.revokeError = null;
    },
    clearExtendError(state) {
      state.extendError = null;
    },
    clearLastShareResult(state) {
      state.lastShareResult = null;
    },
    // Supprime localement un accès (pour mise à jour optimiste UI)
    removeAccessEntryLocal(state, action) {
      const { documentId, doctorId } = action.payload;
      if (state.accessByDocument[documentId]) {
        state.accessByDocument[documentId] = state.accessByDocument[
          documentId
        ].filter((entry) => String(entry.doctorId) !== String(doctorId));
      }
    },
    // Supprime localement tous les accès d'un médecin sur tous les documents
    removeAllDoctorAccessLocal(state, action) {
      const { doctorId } = action.payload;
      Object.keys(state.accessByDocument).forEach((docId) => {
        state.accessByDocument[docId] = state.accessByDocument[docId].filter(
          (entry) => String(entry.doctorId) !== String(doctorId)
        );
      });
      // Retire aussi des médecins avec accès patient
      state.patientDoctorsWithAccess = state.patientDoctorsWithAccess.filter(
        (d) => String(d.doctorId) !== String(doctorId)
      );
    },
    // Reset complet sharing state
    resetSharingState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // ── SHARE DOCUMENT ──
    builder
      .addCase(shareDocument.pending, (state) => {
        state.shareLoading = true;
        state.shareError = null;
      })
      .addCase(shareDocument.fulfilled, (state, action) => {
        state.shareLoading = false;
        state.lastShareResult = action.payload;
      })
      .addCase(shareDocument.rejected, (state, action) => {
        state.shareLoading = false;
        state.shareError = action.payload || "Erreur lors du partage";
      });

    // ── SHARE DOCUMENTS (batch) ──
    builder
      .addCase(shareDocuments.pending, (state) => {
        state.shareLoading = true;
        state.shareError = null;
      })
      .addCase(shareDocuments.fulfilled, (state, action) => {
        state.shareLoading = false;
        state.lastShareResult = action.payload;
      })
      .addCase(shareDocuments.rejected, (state, action) => {
        state.shareLoading = false;
        state.shareError = action.payload || "Erreur lors du partage multiple";
      });

    // ── FETCH DOCUMENT ACCESS LIST ──
    builder
      .addCase(fetchDocumentAccessList.pending, (state, action) => {
        const docId = action.meta.arg?.documentId;
        if (docId) {
          state.accessStatusByDocument[docId] = {
            loading: true,
            error: null,
          };
        }
      })
      .addCase(fetchDocumentAccessList.fulfilled, (state, action) => {
        const docId = action.payload?.documentId;
        if (docId) {
          state.accessByDocument[docId] =
            action.payload?.accessList?.accessList ||
            action.payload?.accessList ||
            [];
          state.accessStatusByDocument[docId] = {
            loading: false,
            error: null,
          };
        }
      })
      .addCase(fetchDocumentAccessList.rejected, (state, action) => {
        const docId = action.meta.arg?.documentId;
        if (docId) {
          state.accessStatusByDocument[docId] = {
            loading: false,
            error: action.payload || "Erreur chargement accès",
          };
        }
      });

    // ── REVOKE DOCUMENT ACCESS ──
    builder
      .addCase(revokeDocumentAccess.pending, (state) => {
        state.revokeLoading = true;
        state.revokeError = null;
      })
      .addCase(revokeDocumentAccess.fulfilled, (state, action) => {
        state.revokeLoading = false;
        const { documentId, revokedDoctorIds = [] } = action.payload || {};
        if (documentId && state.accessByDocument[documentId]) {
          // Marque comme REVOKED ou retire les entrées
          state.accessByDocument[documentId] = state.accessByDocument[
            documentId
          ].filter(
            (entry) =>
              !revokedDoctorIds
                .map(String)
                .includes(String(entry.doctorId))
          );
        }
      })
      .addCase(revokeDocumentAccess.rejected, (state, action) => {
        state.revokeLoading = false;
        state.revokeError = action.payload || "Erreur lors de la révocation";
      });

    // ── REVOKE ALL ACCESS FOR DOCTOR ──
    builder
      .addCase(revokeAllAccessForDoctor.pending, (state) => {
        state.revokeLoading = true;
        state.revokeError = null;
      })
      .addCase(revokeAllAccessForDoctor.fulfilled, (state, action) => {
        state.revokeLoading = false;
        const { doctorId } = action.payload || {};
        if (doctorId) {
          // Retire de tous les documents
          Object.keys(state.accessByDocument).forEach((docId) => {
            state.accessByDocument[docId] = state.accessByDocument[
              docId
            ].filter((entry) => String(entry.doctorId) !== String(doctorId));
          });
          // Retire des médecins avec accès
          state.patientDoctorsWithAccess = state.patientDoctorsWithAccess.filter(
            (d) => String(d.doctorId) !== String(doctorId)
          );
        }
      })
      .addCase(revokeAllAccessForDoctor.rejected, (state, action) => {
        state.revokeLoading = false;
        state.revokeError =
          action.payload || "Erreur révocation tous accès";
      });

    // ── EXTEND DOCUMENT ACCESS ──
    builder
      .addCase(extendDocumentAccess.pending, (state) => {
        state.extendLoading = true;
        state.extendError = null;
      })
      .addCase(extendDocumentAccess.fulfilled, (state, action) => {
        state.extendLoading = false;
        const { documentId, doctorId, expiresAt, expiresInHours } =
          action.payload || {};
        if (documentId && state.accessByDocument[documentId]) {
          state.accessByDocument[documentId] = state.accessByDocument[
            documentId
          ].map((entry) =>
            String(entry.doctorId) === String(doctorId)
              ? { ...entry, expiresAt, expiresInHours }
              : entry
          );
        }
      })
      .addCase(extendDocumentAccess.rejected, (state, action) => {
        state.extendLoading = false;
        state.extendError = action.payload || "Erreur extension accès";
      });

    // ── GET DOCTOR SHARED DOCUMENTS ──
    builder
      .addCase(getDoctorSharedDocuments.pending, (state) => {
        state.doctorSharedDocumentsLoading = true;
        state.doctorSharedDocumentsError = null;
      })
      .addCase(getDoctorSharedDocuments.fulfilled, (state, action) => {
        state.doctorSharedDocumentsLoading = false;
        state.doctorSharedDocuments = action.payload?.documents || [];
        state.doctorSharedDocumentsTotal = action.payload?.total || 0;
      })
      .addCase(getDoctorSharedDocuments.rejected, (state, action) => {
        state.doctorSharedDocumentsLoading = false;
        state.doctorSharedDocumentsError =
          action.payload || "Erreur chargement documents partagés";
      });

    // ── GET PATIENT DOCTORS WITH ACCESS ──
    builder
      .addCase(getPatientDoctorsWithAccess.pending, (state) => {
        state.patientDoctorsWithAccessLoading = true;
        state.patientDoctorsWithAccessError = null;
      })
      .addCase(getPatientDoctorsWithAccess.fulfilled, (state, action) => {
        state.patientDoctorsWithAccessLoading = false;
        state.patientDoctorsWithAccess = action.payload?.doctors || [];
      })
      .addCase(getPatientDoctorsWithAccess.rejected, (state, action) => {
        state.patientDoctorsWithAccessLoading = false;
        state.patientDoctorsWithAccessError =
          action.payload || "Erreur chargement médecins";
      });

    // ── GET SHARING STATISTICS ──
    builder
      .addCase(getSharingStatistics.pending, (state) => {
        state.sharingStatisticsLoading = true;
        state.sharingStatisticsError = null;
      })
      .addCase(getSharingStatistics.fulfilled, (state, action) => {
        state.sharingStatisticsLoading = false;
        state.sharingStatistics =
          action.payload?.statistics || action.payload || null;
      })
      .addCase(getSharingStatistics.rejected, (state, action) => {
        state.sharingStatisticsLoading = false;
        state.sharingStatisticsError =
          action.payload || "Erreur chargement statistiques";
      });
  },
});

export const {
  clearShareError,
  clearRevokeError,
  clearExtendError,
  clearLastShareResult,
  removeAccessEntryLocal,
  removeAllDoctorAccessLocal,
  resetSharingState,
} = sharingSlice.actions;

export default sharingSlice.reducer;